// UI-local state: things only the HUD cares about (tooltips, the sources drawer focus, which
// notification is open). App state lives in services; nothing here is data.

import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import type { SourceId } from '../core/schema.ts';

/** Sources the drawer is focused on (a clicked source chip). Empty = drawer closed. */
export const sourceFocus = signal<SourceId[]>([]);

/** Notification card currently expanded. */
export const openNote = signal<string | null>(null);

/**
 * Opens a card, or closes it when it is already open. Opening also drops the hover tip: the
 * pointer still rests on the icon, and the tip would sit on top of the card it just expanded.
 */
export function toggleNote(id: string): void {
  if (openNote.value === id) {
    openNote.value = null;
    return;
  }

  openNote.value = id;
  tip.value = null;
}

/** The "view the world as" perspective menu under the leader medallion. */
export const switcherOpen = signal(false);

/** HUD size in its own (unscaled) CSS pixels plus the UI scale, kept current by Hud. */
export const hudSize = signal({ w: 0, h: 0, scale: 1 });

// ─── Floating tooltip ────────────────────────────────────────────────────────────────────
//
// One tooltip layer at the HUD root so tips are never clipped by scrolling panels.
// Anchors are stored as viewport rects; the layer converts them to HUD coordinates.

export interface TipState {
  rect: { left: number; top: number; right: number; bottom: number };
  content: ComponentChildren;
  /** Preferred side; the layer flips when it would leave the HUD. */
  side: 'below' | 'above' | 'left' | 'right';
}

export const tip = signal<TipState | null>(null);

// ─── Sources on screen ───────────────────────────────────────────────────────────────────
//
// Every SourceChip registers its ids while mounted, so the Sources drawer can list "sources for
// what you are looking at". Counts are coalesced into one signal write per microtask.

const counts = new Map<SourceId, number>();
let flushQueued = false;

export const onScreenSources = signal<SourceId[]>([]);

function queueFlush(): void {
  if (flushQueued) {
    return;
  }
  flushQueued = true;
  queueMicrotask(() => {
    flushQueued = false;
    onScreenSources.value = [...counts.keys()].sort();
  });
}

export function registerSources(ids: readonly SourceId[]): () => void {
  for (const id of ids) {
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  queueFlush();

  return () => {
    for (const id of ids) {
      const n = (counts.get(id) ?? 1) - 1;
      if (n <= 0) {
        counts.delete(id);
      } else {
        counts.set(id, n);
      }
    }
    queueFlush();
  };
}
